import { useTranslation } from 'react-i18next';
import { Button, Badge } from '@/components/v2/ui';
import { type ImageOptimizeState } from './useImageOptimizeState';

interface Props {
  state: ImageOptimizeState;
  onReanalyze: () => void;
}

export function AnalysisResults({ state, onReanalyze }: Props) {
  const { t } = useTranslation();
  const r = state.analysisResult!;

  return (
    <div className="space-y-3 text-sm">
      {r.detected_text.length > 0 && (
        <div>
          <span className="font-medium">{t('imageOptimize.analysis.detectedText')} ({r.detected_text.length})</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {r.detected_text.map((txt, i) => <Badge key={i} variant="warning" size="sm">{txt}</Badge>)}
          </div>
        </div>
      )}

      {r.dominant_colors.length > 0 && (
        <div>
          <span className="font-medium">{t('imageOptimize.analysis.dominantColors')}</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {r.dominant_colors.map((c, i) => (
              <span key={i} className="flex items-center gap-1 text-xs">
                <span className="w-4 h-4 rounded-full border border-neutral-200" style={{ backgroundColor: c.hex }} />
                {c.name} ({c.percentage}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {r.elements.length > 0 && (
        <div>
          <span className="font-medium">{t('imageOptimize.analysis.elements')}</span>
          {r.elements.map((el, i) => (
            <p key={i} className="text-xs text-neutral-600"><strong>{el.type}</strong> - {el.description}</p>
          ))}
        </div>
      )}

      {r.tags.length > 0 && (
        <div>
          <span className="font-medium">{t('imageOptimize.analysis.tags')}</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {r.tags.map((tag, i) => <Badge key={i} variant="default" size="sm">{tag}</Badge>)}
          </div>
        </div>
      )}

      <div>
        <Badge variant="primary" size="sm">{r.style_category}</Badge>
        <p className="text-xs text-neutral-600 mt-1">{r.style_dna_summary}</p>
        {r.mood_and_atmosphere?.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {r.mood_and_atmosphere.keywords.map((kw, i) => (
              <Badge key={i} variant="info" size="sm">{kw}</Badge>
            ))}
          </div>
        )}
      </div>

      <Button variant="ghost" size="sm" className="w-full" onClick={onReanalyze}>
        {t('imageOptimize.analysis.reanalyze')}
      </Button>
    </div>
  );
}
